import { useCallback, useState } from 'react';

import { useStoreContext } from 'features/store';
import { DEFAULT_SEARCH_RADIUS_IN_METERS } from 'features/store/constants';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import { useNearbyRestaurants } from 'features/store/pages/store-locator/hooks/helpers/use-restaurants';
import { ISearchNearbyRestaurants } from 'features/store/pages/store-locator/hooks/types';

import { ILocation } from '@rbi-ctg/frontend';
import { IRestaurant } from '@rbi-ctg/store';
import { FilterRestaurantType, OperationalStatus } from 'generated/graphql-gateway';
import { useGeolocation } from 'state/geolocation';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { isValidPosition } from 'utils/geolocation';

export const useFindAvailablePickupRestaurant = () => {
  const { isStoreAvailableForPickup } = useStoreAvailabilityFns();

  const { fetch: fetchNearbyRestaurants } = useNearbyRestaurants();
  const { isPermissionGranted, userCoordinates } = useGeolocation();
  const { store: closedStore } = useStoreContext();

  const [isLoading, setIsLoading] = useState(false);

  const [availableRestaurant, setAvailableRestaurant] = useState<IRestaurant | null>(null);

  const defaultDiagonal =
    useFlag(LaunchDarklyFlag.RADIUS_FOR_STORE_LOCATOR) || DEFAULT_SEARCH_RADIUS_IN_METERS;

  // If we have permission and user coordinates, use them. Otherwise, use the closed store coordinates.
  const coordinatesToUse: ILocation = {
    lat: isPermissionGranted && userCoordinates ? userCoordinates.lat : closedStore.latitude || 0,
    lng: isPermissionGranted && userCoordinates ? userCoordinates.lng : closedStore.longitude || 0,
  };

  // TODO: Unify restaurant search functionality across the codebase
  const searchNearbyRestaurantsAsync = useCallback(
    async ({ location }: ISearchNearbyRestaurants): Promise<IRestaurant[]> => {
      if (!closedStore.number && !isValidPosition(location)) {
        return [];
      }

      const nearbyRestaurants = ((await fetchNearbyRestaurants(
        {
          coordinates: {
            userLat: Number(location.lat.toFixed(3)),
            userLng: Number(location.lng.toFixed(3)),
            searchRadius: defaultDiagonal,
          },
          first: 10,
          filter: FilterRestaurantType.NEARBY,
          status: OperationalStatus.OPEN,
          radiusStrictMode: true,
        },
        { allowFromCache: false }
      )) as unknown) as IRestaurant[];

      return nearbyRestaurants ?? [];
    },
    [closedStore.number, defaultDiagonal, fetchNearbyRestaurants]
  );

  const clearSearchState = () => {
    setIsLoading(false);
    setAvailableRestaurant(null);
  };

  const findFirstOrderableRestaurantForPickUp = async () => {
    try {
      setIsLoading(true);
      setAvailableRestaurant(null);

      const nearbyRestaurants = await searchNearbyRestaurantsAsync({
        location: coordinatesToUse,
      });

      if (!nearbyRestaurants.length) {
        clearSearchState();
        return;
      }

      for (const restaurant of nearbyRestaurants) {
        if (restaurant._id === closedStore._id) {
          continue;
        }

        const isAvailable = isStoreAvailableForPickup(restaurant);
        if (isAvailable) {
          setAvailableRestaurant(restaurant);
          return;
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    findFirstOrderableRestaurantForPickUp,
    isLoading,
    availableRestaurant,
    clearSearchState,
  };
};
